import React from 'react';

import { observer } from 'mobx-react';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';

import { DetailsRowContainer, TitleText } from 'components/Entities/AddEntityModal/styles';

import { useStore } from 'utils/hooks/store';

import API from 'utils/api';
import { getErrorMessage } from 'utils/api/errors';
import Logger from 'utils/logger';

import { AxiosError } from 'axios';

import { OrgUnit, isIntake, unitPath } from 'containers/OrganisationStructure/types';

import UnitPickerField from 'containers/OrganisationStructure/components/UnitPickerField';

export interface ChangeUnitModalProps {
  userUuid: string;
  userName: string;
  currentUnitUuid: string | null;
  isAccountOwner: boolean;
  onClose: () => void;
}

const unavailable = (units: OrgUnit[], isAccountOwner: boolean): Map<string, string> => {
  const reasons = new Map<string, string>();
  const walk = (list: OrgUnit[], depth: number): void => {
    for (const unit of list) {
      if (isIntake(unit)) reasons.set(unit.uuid, 'Intake only holds records waiting to be placed');
      else if (isAccountOwner && depth > 0) reasons.set(unit.uuid, 'An Account Owner sits at the top unit');
      if (unit.children) walk(unit.children, depth + 1);
    }
  };
  walk(units, 0);
  return reasons;
};

const ChangeUnitModal: React.FunctionComponent<ChangeUnitModalProps> = ({
  userUuid,
  userName,
  currentUnitUuid,
  isAccountOwner,
  onClose,
}) => {
  const { notification, organisationStructure, userDetails } = useStore();
  const [unitUuid, setUnitUuid] = React.useState<string | null>(currentUnitUuid);
  const [saving, setSaving] = React.useState(false);

  const units = organisationStructure.tree;

  const save = async (): Promise<void> => {
    if (!unitUuid) return;
    setSaving(true);
    try {
      const { data } = await API.units.setBinding({ user: userUuid, unit: unitUuid })();
      userDetails.setHomeUnit(data.unit);
    } catch (e) {
      Logger.error('Could not set a binding for a user', e);
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Could not change the unit'));
      setSaving(false);
      return;
    }
    notification.enqueueSuccessSnackbar(`${userName} is now in ${unitPath(units, unitUuid)}`);
    setSaving(false);
    onClose();
  };

  return (
    <CustomModal
      openModal
      onClose={onClose}
      modalTitle={`${currentUnitUuid ? 'Change unit' : 'Set unit'} for ${userName}`}
      cancelButton={
        <Button onClick={onClose} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button
          onClick={save}
          disabled={!unitUuid || unitUuid === currentUnitUuid || saving}
          variant="contained"
          size="large"
        >
          {currentUnitUuid ? 'Change unit' : 'Set unit'}
        </Button>
      }
    >
      <DetailsRowContainer>
        <TitleText as="label" htmlFor="user-unit-change">
          Unit
        </TitleText>
        <UnitPickerField
          id="user-unit-change"
          units={units}
          disabledReasons={unavailable(units, isAccountOwner)}
          value={unitUuid}
          onChange={setUnitUuid}
          placeholder="Select a unit"
          label="Unit"
        />
      </DetailsRowContainer>
    </CustomModal>
  );
};

export default observer(ChangeUnitModal);
